import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import recetaService, { RecetaListItemDto } from '../../services/recetaService';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import PDFViewer from '../../components/shared/PDFViewer';

const Recetas: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<RecetaListItemDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [printingCodigo, setPrintingCodigo] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{
    el: HTMLElement;
    row: RecetaListItemDto;
  } | null>(null);
  const [pdfPreview, setPdfPreview] = useState<{
    url: string;
    codigo: string;
    title: string;
  } | null>(null);
  const pdfPreviewRef = useRef(pdfPreview);
  pdfPreviewRef.current = pdfPreview;

  const fetchData = useCallback(
    async (pageNum: number, pageSize: number, search: string) => {
      try {
        setLoading(true);
        const data = await recetaService.getRecetas({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
        });
        setItems(data.items);
        setTotalCount(data.totalCount);
      } catch {
        enqueueSnackbar('Error al cargar recetas', { variant: 'error' });
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    const t = setTimeout(() => fetchData(page, rowsPerPage, searchTerm), searchTerm ? 300 : 0);
    return () => clearTimeout(t);
  }, [page, rowsPerPage, searchTerm, fetchData]);

  useEffect(
    () => () => {
      const p = pdfPreviewRef.current;
      if (p?.url) URL.revokeObjectURL(p.url);
    },
    [],
  );

  const handleDelete = async (codigo: string) => {
    if (!window.confirm('¿Eliminar esta receta?')) return;
    try {
      await recetaService.remove(codigo);
      enqueueSnackbar('Receta eliminada', { variant: 'success' });
      fetchData(page, rowsPerPage, searchTerm);
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al eliminar receta';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleImprimirPdf = async (row: RecetaListItemDto) => {
    const codigo = row.codigoReceta.trim();
    if (!codigo) return;
    setPrintingCodigo(codigo);
    try {
      const blob = await recetaService.getPdf(codigo);
      const url = URL.createObjectURL(blob);
      if (pdfPreviewRef.current?.url) URL.revokeObjectURL(pdfPreviewRef.current.url);
      setPdfPreview({
        url,
        codigo,
        title: `Receta ${codigo}${row.descripcion ? ` — ${row.descripcion.trim()}` : ''}`,
      });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo generar el PDF de la receta'), {
        variant: 'error',
      });
    } finally {
      setPrintingCodigo(null);
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview?.url) URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Recetas
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/produccion/recetas/nuevo')}
            >
              Nueva receta
            </Button>
          </Stack>

          <TextField
            fullWidth
            placeholder="Buscar por código o descripción"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Producto terminado</TableCell>
                  <TableCell align="right">Componentes</TableCell>
                  <TableCell align="right">Costo / UM base</TableCell>
                  <TableCell>UM base</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                  <TableCell>Últ. modificación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.codigoReceta} hover>
                    <TableCell>{row.codigoReceta}</TableCell>
                    <TableCell>{row.descripcion}</TableCell>
                    <TableCell>
                      {row.codigoProductoTerminado
                        ? `${row.codigoProductoTerminado} — ${row.descripcionProductoTerminado ?? ''}`
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{row.cantidadComponentes}</TableCell>
                    <TableCell align="right">
                      {row.costoUnitario != null ? formatCurrency(row.costoUnitario) : '—'}
                    </TableCell>
                    <TableCell>{row.unidadMedidaBase?.trim() || '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 1 }}>
                      <Stack direction="row" spacing={0} justifyContent="flex-end" alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            aria-label={`Editar receta ${row.codigoReceta}`}
                            onClick={() => navigate(`/produccion/recetas/${encodeURIComponent(row.codigoReceta)}`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Más acciones">
                          <IconButton
                            size="small"
                            aria-label={`Más acciones receta ${row.codigoReceta}`}
                            onClick={(e) => setMenuAnchor({ el: e.currentTarget, row })}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {row.fechaUltModificacion ? formatDateTime(row.fechaUltModificacion) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No hay recetas para mostrar.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Filas"
          />
        </CardContent>
      </Card>

      <Menu
        anchorEl={menuAnchor?.el ?? null}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          disabled={printingCodigo === menuAnchor?.row.codigoReceta.trim()}
          onClick={() => {
            const row = menuAnchor?.row;
            setMenuAnchor(null);
            if (row) void handleImprimirPdf(row);
          }}
        >
          <ListItemIcon>
            {printingCodigo === menuAnchor?.row.codigoReceta.trim() ? (
              <CircularProgress size={18} />
            ) : (
              <PrintIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>Imprimir PDF</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const codigo = menuAnchor?.row.codigoReceta;
            setMenuAnchor(null);
            if (codigo) {
              navigate(`/produccion/recetas/nuevo?copiarDe=${encodeURIComponent(codigo)}`);
            }
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copiar</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const codigo = menuAnchor?.row.codigoReceta;
            setMenuAnchor(null);
            if (codigo) void handleDelete(codigo);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Eliminar</ListItemText>
        </MenuItem>
      </Menu>

      {pdfPreview && (
        <PDFViewer
          pdfUrl={pdfPreview.url}
          onClose={closePdfPreview}
          title={pdfPreview.title}
          documentId={pdfPreview.codigo}
          documentType="informeCliente"
          downloadFileName={`receta_${pdfPreview.codigo.trim().replace(/\s+/g, '_')}.pdf`}
        />
      )}
    </Box>
  );
};

export default Recetas;
